import { Input, Marker, Autocomplete, Tooltip } from '@design-systems/canvas-ui'
import { TruncatedValueTooltip } from './TruncatedValueTooltip'

export const AutocompleteInput = (props) => {
  const rawOptions = Array.isArray(props.options) ? props.options : []
  const additionalClassName =
    `${props.class ?? ''} ${props.className ?? ''}`.trim()

  const options = rawOptions
    .filter(
      (o) =>
        o &&
        typeof o === 'object' &&
        (o.id != null || o.value != null || o.label != null) &&
        !(o.label === 'Please select an option' && o.value === ''),
    )
    .map((o) => {
      const rawId = o.id != null ? o.id : o.value != null ? o.value : o.label
      return {
        ...o,
        id: String(rawId),
        value: o.value != null ? o.value : o.label,
        label: o.label != null ? o.label : String(o.value),
        isDisabled: o.isDisabled ?? o.disabled,
      }
    })

  const findByValue = (val) => {
    if (val == null) return undefined
    return options.find(
      (o) =>
        o.value === val ||
        o.id === String(val) ||
        o.label === val ||
        String(o.value) === String(val),
    )
  }

  if (props.readOnly) {
    return (
      <div className={`lcm-autocomplete-readonly ${additionalClassName}`}>
        {props.multiselect
          ? props?.value?.map((each, i) => (
              <div
                className="lcm-autocomplete-readonly__chip"
                key={`ac-ro-${i}`}
              >
                <TruncatedValueTooltip value={each.label}>
                  <Marker size="sm">
                    <span className="lcm-truncated-value-tooltip__value">
                      {each.label}
                    </span>
                  </Marker>
                </TruncatedValueTooltip>
              </div>
            ))
          : props.value && (
              <div className="lcm-autocomplete-readonly__chip">
                <TruncatedValueTooltip value={props.value}>
                  <Marker size="sm">
                    <span className="lcm-truncated-value-tooltip__value">
                      {props.value}
                    </span>
                  </Marker>
                </TruncatedValueTooltip>
              </div>
            )}
      </div>
    )
  }

  if (props.multiselect) {
    const selectedOptions = Array.isArray(props.value)
      ? props.value
          .map((v) => {
            const match = findByValue(v?.value ?? v?.id ?? v?.label ?? v)
            if (match) return match.isDisabled ? undefined : match
            const rawId = v?.id ?? v?.value ?? v?.label ?? v
            return {
              id: String(rawId),
              value: v?.value ?? v?.id ?? v,
              label: v?.label ?? String(v?.value ?? v?.id ?? v),
            }
          })
          .filter(Boolean)
      : []
    const firstSelectedOption = selectedOptions[0]
    const additionalSelectionCount = Math.max(selectedOptions.length - 1, 0)
    const additionalSelectionLabels = selectedOptions
      .slice(1)
      .map((option) => option.label)
      .join(', ')

    const handleSelectedDisplayMouseDown = (event) => {
      event.preventDefault()
      event.currentTarget.parentElement?.querySelector('input')?.focus()
    }

    return (
      <div className="lcm-multiselect-wrapper">
        <Input.MultiSelect
          className={`lcm-autocomplete-control lcm-multiselect-control ${additionalClassName} hc8-mb-none`}
          id={`${props.name}`}
          options={options}
          value={selectedOptions}
          isErrored={!!props.error}
          errorText={props.errorText}
          placeholder={
            props.defaultPlaceholder
              ? props.defaultPlaceholder
              : 'Please select an option'
          }
          isDisabled={props.disabled}
          onChange={(nextOptions) => {
            const mapped = (nextOptions || [])
              .map((opt) => findByValue(opt?.value ?? opt?.id) || opt)
              .filter((opt) => !opt.isDisabled)
            props.onUpdate(mapped)
          }}
        />
        {firstSelectedOption && (
          <div
            className="lcm-multiselect-selected-display"
            onMouseDown={handleSelectedDisplayMouseDown}
          >
            <div className="lcm-multiselect-selected-display__first">
              <TruncatedValueTooltip value={firstSelectedOption.label}>
                <Marker
                  className="lcm-multiselect-selected-display__marker"
                  color="grey"
                  size="sm"
                >
                  <span className="lcm-truncated-value-tooltip__value">
                    {firstSelectedOption.label}
                  </span>
                </Marker>
              </TruncatedValueTooltip>
            </div>
            {additionalSelectionCount > 0 && (
              <Tooltip
                className="lcm-multiselect-selected-display__count-tooltip"
                content={additionalSelectionLabels}
                location="top"
              >
                <Marker
                  className="lcm-multiselect-selected-display__count"
                  color="grey"
                  size="sm"
                >
                  +{additionalSelectionCount}
                </Marker>
              </Tooltip>
            )}
          </div>
        )}
      </div>
    )
  }

  const selectedId = (() => {
    if (props.value == null || props.value === '') return ''
    if (typeof props.value === 'object') {
      const match = findByValue(
        props.value.value ?? props.value.id ?? props.value.label,
      )
      return match && !match.isDisabled ? match.id : ''
    }
    const match = findByValue(props.value)
    return match && !match.isDisabled ? match.id : ''
  })()

  return (
    <Autocomplete
      className={`lcm-autocomplete-control ${additionalClassName} hc8-mb-none`}
      id={`${props.name}`}
      options={options}
      value={selectedId}
      isErrored={!!props.error}
      errorText={props.errorText}
      placeholder={
        props.defaultPlaceholder
          ? props.defaultPlaceholder
          : 'Please select an option'
      }
      filter="contains"
      openOnFocus
      isDisabled={props.disabled}
      onChange={(selected) => {
        if (!selected) {
          props.onUpdate(undefined)
          return
        }
        if (selected.isDisabled) return
        const opt = findByValue(selected.value ?? selected.id) || selected
        props.onUpdate(opt)
      }}
    />
  )
}
